import { useState, useMemo, useEffect } from 'react';
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { formatDate, maskAddress, parseDateAndTime, parseTime, shouldMaskData, shouldMaskDataReverse } from '../../helpers/UtilityHelper';
import { formatCurrency } from '../../common/util';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFDateRangePickerField, RHFFileField, RHFTextField } from "../../common/hook-form";
import useNdr from "../../hooks/useNdr";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link } from 'react-router-dom';
import { CourierFilter, MerchantFilterCached } from '../../common/ProshipFormComponents';
import MUIModal from '../../components/MuiModal';
import Loader from '../../common/Loader';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PageTitle from '../../components/PageTitle';
import { apiConstants, getDownload, postMultipart } from '../../common/constants';
import { useSnackbar } from 'notistack';
import { CalendarMonthSharp, DownloadRounded } from '@mui/icons-material';
import AppointmentDelivery from './AddAppDelivery';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import jwt_decode from "jwt-decode";
import { getToken } from '../../helpers/ReduxHelpers';
import StatusChip from '../../common/StatusChip';
import OrderRowActions from '../../common/OrderRowActions';
import { FieldLabel, FilterField, FilterDateField } from '../../common/FilterBar';
import CopyIcon from '../../common/CopyIcon';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
        {children}
    </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const AppointmentDeliveries = () => {
    const methods = useForm({
        defaultValues: {
            status: "", courier: null, merchant: '', reference: '',
            awb: '', city: '', state: '', fromDate: '', toDate: '', channel: ''
        }
    });
    const { handleSubmit, reset } = methods;

    const [page, setPage] = useState(0);
    const [filterChange, setFiltersChange] = useState(0);
    const [filters, setFilters] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [reload, setReload] = useState(0);
    const [delConfirm, setDelConfirm] = useState(false);
    const [appDetails, setAppDetails] = useState(null);
    const { report } = useSelector(state => state);
    const { piiAccessAllow } = jwt_decode(getToken());

    const request = useMemo(() => {
        const includes = "appointmentDeliveryDate,edd,orderDate,id,reference,invoiceValue,pods,shippingLabelId,awbRegisteredDate,merchantPricing.zone,merchantDetail,courierDetail,pickupDetails.from_pincode,pickupDetails.from_city,deliveryDetails.to_pincode,deliveryDetails.to_state,deliveryDetails.to_city,channelName,orderStatus,shipmentDetail,paymentMode,awbNumber,referenceNumber,merchantPricing.price,additionalDocuments";
        const andfilter = [];
        const orFilter = [];
        andfilter.push({ "field": "shipmentType", operator: "eq", value: "B2B" });
        andfilter.push({ "field": "appointmentDelivery", operator: "eq", value: true });
        andfilter.push({ "field": "terminalStatus", "operator": "exists", "value": false });
        if (filters.awb) andfilter.push({ "field": "awbNumber", "operator": "eq", "value": filters.awb });
        if (filters.city) andfilter.push({ "field": "deliveryDetails.to_city", "operator": "eq", "value": filters.city });
        if (filters.state) andfilter.push({ "field": "deliveryDetails.to_state", "operator": "eq", "value": filters.state });
        if (filters.merchant) andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.merchant + '\')' });
        if (filters.courier) andfilter.push({ "field": "courier.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.courier + '\')' });
        if (filters.reference) { orFilter.push({ "field": "reference", "operator": "like", "value": filters.reference + ".*" }); orFilter.push({ "field": "shippingLabelId", "operator": "like", "value": filters.reference + ".*" }); }
        if (filters.fromDate) andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(filters.fromDate, 'yyyy-MM-dd') });
        if (filters.toDate) andfilter.push({ field: "orderDate", operator: "lte", value: formatDate(filters.toDate, 'yyyy-MM-dd') });
        if (filters.channel) andfilter.push({ field: "channelName", operator: "eq", value: "Selfship" });
        return { "andfilter": andfilter, "orfilter": orFilter, "offset": (page) * sizePerPage, "limit": sizePerPage, "skipCount": true, "include": includes };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterChange, page, sizePerPage, reload]);

    let { data = [], isFetching, refetch } = useNdr({ request: request });
    useEffect(() => { refetch(); }, [reload]); // eslint-disable-line

    const handleDelivery = (val) => { setDelConfirm(true); setAppDetails(val); };
    const onSubmit = (data) => { setFiltersChange(filterChange + 1); setFilters(data); };

    const columns = [
        {
            field: 'merchant',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 140, flex: 1,
            renderCell: ({ row }) => <CellLine bold>{row?.merchantDetail?.name || '—'}</CellLine>,
        },
        {
            field: 'orderreference',
            headerName: 'Order Details', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 170, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.reference || '—'}</CellLine>
                    {row.awbRegisteredDate && <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{parseDateAndTime(row.awbRegisteredDate)}</Box>}
                </CellStack>
            ),
        },
        {
            field: 'awb',
            headerName: 'AWB', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 160, flex: 1.1,
            renderCell: ({ row }) => row?.awb_number ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component={Link} to={`/order-detail/${row.awb_number}`}
                        sx={{ fontSize: '12px', fontWeight: 700, color: '#1d4ed8', textDecoration: 'none', borderBottom: '1px dashed #93c5fd', pb: '1px', '&:hover': { color: '#1e40af', borderBottomStyle: 'solid' } }}>
                        {row.awb_number}
                    </Box>
                    <CopyIcon text={row.awb_number} />
                </Box>
            ) : <CellLine muted>—</CellLine>,
        },
        {
            field: 'pincode',
            headerName: 'Pickup & Drop', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: 'text.primary' }}>
                        <Box component="span" sx={{ fontSize: '11px', color: '#16a34a', flexShrink: 0, mt: '1px' }}>▲</Box>
                        <Box>
                            {shouldMaskDataReverse(report, row, piiAccessAllow)
                                ? `${row.pickup_details?.from_pincode}, ${maskAddress(row.pickup_details?.from_city)}`
                                : `${row.pickup_details?.from_pincode}, ${row.pickup_details?.from_city}`}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: 'text.secondary' }}>
                        <Box component="span" sx={{ fontSize: '11px', color: '#dc2626', flexShrink: 0, mt: '1px' }}>▼</Box>
                        <Box>
                            {shouldMaskData(report, row, piiAccessAllow)
                                ? `${row.delivery_details?.to_pincode}, ${maskAddress(row.delivery_details?.to_city)}`
                                : `${row.delivery_details?.to_pincode}, ${row.delivery_details?.to_city}`}
                        </Box>
                    </Box>
                </CellStack>
            ),
        },
        {
            field: 'courier',
            headerName: 'Courier', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row?.courierDetail?.parent || '—'}</CellLine>
                    {row?.courierDetail?.prozoId && <CellLine muted ellipsis>{row.courierDetail.prozoId}</CellLine>}
                </CellStack>
            ),
        },
        {
            field: 'statusDetails',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1,
            renderCell: ({ row }) => <StatusChip status={row?.orderStatus} />,
        },
        {
            field: 'weight',
            headerName: 'Weight', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 110,
            renderCell: ({ row }) => {
                const w = row.shipment_detail?.[0]?.item_weight;
                return <Box sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>{w ? `${(w / 1000).toFixed(2)} kg` : '—'}</Box>;
            },
        },
        {
            field: 'edd',
            headerName: 'EDD', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => <CellLine muted>{row.edd ? parseDateAndTime(row.edd) : '—'}</CellLine>,
        },
        {
            field: 'invoice',
            headerName: 'Invoice', headerAlign: 'right', align: 'right',
            hideSortIcons: true, minWidth: 110, flex: 0.8,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                    {row.invoice_value ? formatCurrency(row.invoice_value) : '—'}
                </Box>
            ),
        },
        {
            field: 'appointment_del_start',
            headerName: 'Appointment', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 190, flex: 1.3,
            renderCell: ({ row }) => {
                const d = row?.appointmentDeliveryData;
                if (!d?.appointment_del_start) return <CellLine muted>Not scheduled</CellLine>;
                const dateStr = formatDate(d.appointment_del_start, 'dd MMM, yyyy');
                const timeStr = d.appointment_del_end
                    ? `${parseTime(d.appointment_del_start)} – ${parseTime(d.appointment_del_end)}`
                    : parseTime(d.appointment_del_start);
                return (
                    <CellStack>
                        <CellLine bold>{dateStr}</CellLine>
                        <CellLine muted>{timeStr}</CellLine>
                    </CellStack>
                );
            },
        },
        {
            field: 'action',
            headerName: '', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => (
                <OrderRowActions
                    actions={[
                        { key: 'view', label: 'View details', icon: <VisibilityIcon fontSize="small" />, href: `/order-detail/${row.awb_number}` },
                        { key: 'schedule', label: 'Reschedule appointment', icon: <CalendarMonthSharp fontSize="small" />, onClick: () => handleDelivery(row), color: 'primary', dividerBefore: true },
                    ]}
                />
            ),
        },
    ];

    return (
        <>
            <PageTitle>Appointment Deliveries</PageTitle>

            <MUIModal
                open={delConfirm}
                handleClose={() => { setDelConfirm(false); setAppDetails(null); }}
                maxWidth="lg"
                title={
                    <Typography variant="subtitle1" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        Reschedule Delivery Appointment — AWB{' '}
                        <Typography component="span" color="error" fontWeight="bold">{appDetails?.awb_number}</Typography>
                        <CopyIcon text={appDetails?.awb_number} />
                    </Typography>
                }
                hideClose={true}
            >
                <AppointmentDelivery rowData={appDetails} setDelConfirm={setDelConfirm} setReload={setReload} />
            </MUIModal>

            {isFetching && <Loader />}

            {/* ── Bulk upload section ───────────────────────────────────── */}
            <Card elevation={0} sx={{ mb: 2, borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Bulk Upload Appointment Delivery</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>Upload a CSV/XLSX file to schedule multiple appointment deliveries at once</Typography>
                </Box>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <BulkUploadAppointmentDelivery setReload={setReload} />
                </CardContent>
            </Card>

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <CourierFilter filterList={"B2B"} />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>Order Number</FieldLabel>
                                <RHFTextField name="reference" label="" placeholder="Order Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>City</FieldLabel>
                                <RHFTextField name="city" label="" placeholder="City" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>State</FieldLabel>
                                <RHFTextField name="state" label="" placeholder="State" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>
                            <FilterDateField>
                                <FieldLabel>Created Date</FieldLabel>
                                <RHFDateRangePickerField name="created_date" label="" placeholder="Select date range" size="small" maxDate={new Date()} />
                            </FilterDateField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Box component="button" type="submit"
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Box>
                                <Box component="button" type="button"
                                    onClick={() => { reset({ status: "", courier: '', merchant: '', reference: '', awb: '', city: '', state: '', fromDate: '', toDate: '', channel: '' }); setFilters([]); setFiltersChange(filterChange + 1); }}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                                    Reset
                                </Box>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={(data?.data && data?.data.length > 0) ? data?.data : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    rowCount={Number.MAX_VALUE}
                    getRowId={(row) => row.id}
                />
            </Box>
        </>
    );
};

export default AppointmentDeliveries;

// ─── Bulk upload sub-component ────────────────────────────────────────────────

const BulkUploadAppointmentDelivery = ({ setReload }) => {
    const methodsBulkUpload = useForm({ defaultValues: { orderFile: null } });
    const { handleSubmit: handleSubmitBulkUpload, reset } = methodsBulkUpload;
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [responseArry, setResponseArry] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);

    const resetForm = () => {
        reset({ orderFile: null });
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
    };

    const onSubmitBulkUpload = async (data) => {
        setIsUploadingFile(true);
        try {
            let form = new FormData();
            form.append('file', data.orderFile[0]);
            await postMultipart(`${apiConstants.BUTL_UPLOAD_APPOINTMENT_DELIVERY}`, form).then(response => {
                if (response.status === 200) {
                    const { mapErrors, mapSuccess } = response.data;
                    setReload(r => r + 1);
                    responseMap(mapErrors, mapSuccess);
                    setIsOpen(true);
                    resetForm();
                }
            }).catch(err => {
                console.error(err);
                setIsUploadingFile(false);
                enqueueSnackbar("Error uploading file", { variant: "error" });
            });
        } catch (err) {
            enqueueSnackbar("Some error occurred", { variant: "error" });
        }
        setIsUploadingFile(false);
    };

    const responseMap = (errors, successes) => {
        let formattedData = [];
        for (let key in errors) { formattedData.push({ id: key, awbNumber: key, status: 'Failed', reason: errors[key] }); }
        for (let key in successes) { formattedData.push({ id: key, awbNumber: key, status: 'Success', reason: successes[key] }); }
        setResponseArry(formattedData);
    };

    const handleDownloadSampleClick = () => {
        setLoading(true);
        getDownload(`${apiConstants.BASE_LOCAL}${apiConstants.DOWNLOAD_SAMPLE_LOCATION}?fileName=B2B_ABD_BULK`)
            .then((response) => {
                setLoading(false);
                const href = URL.createObjectURL(new Blob([response.data], { type: "application/octet-stream" }));
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", "B2B_ABD_BULK.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch(err => { console.error(err); setLoading(false); });
    };

    const resultColumns = [
        {
            field: 'awbNumber',
            headerName: 'AWB', headerAlign: 'left', align: 'left',
            hideSortIcons: true, flex: 0.8,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary' }}>{row.awbNumber}</Box>
                    <CopyIcon text={row.awbNumber} />
                </Box>
            ),
        },
        {
            field: 'status',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 120,
            renderCell: ({ row }) => (
                <Box component="span" sx={{
                    display: 'inline-block', px: '7px', py: '2px', borderRadius: '4px',
                    fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                    bgcolor: row.status === 'Success' ? '#dcfce7' : '#fef2f2',
                    color: row.status === 'Success' ? '#15803d' : '#dc2626',
                    border: `1px solid ${row.status === 'Success' ? '#bbf7d0' : '#fecaca'}`,
                }}>
                    {row.status}
                </Box>
            ),
        },
        {
            field: 'reason',
            headerName: 'Message', headerAlign: 'left', align: 'left',
            hideSortIcons: true, flex: 2,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', py: 0.5 }}>
                    {row.reason?.map((r, i) => (
                        <Box key={i} sx={{ fontSize: '12px', color: 'text.secondary', display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                            <Box component="span" sx={{ color: '#9ca3af', mt: '2px', flexShrink: 0 }}>•</Box>
                            {r}
                        </Box>
                    ))}
                </Box>
            ),
        },
    ];

    return (
        <>
            <MUIModal
                open={isOpen}
                handleClose={() => setIsOpen(false)}
                title={<Typography variant="subtitle1" sx={{ mt: 1 }}>Upload Results</Typography>}
                maxWidth="md"
            >
                <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', mt: 1 }}>
                    <ProzoDataGrid
                        columns={resultColumns}
                        hideFooterPagination={false}
                        autoHeight={true}
                        rows={responseArry || []}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        pagination={true}
                        getRowHeight={() => 'auto'}
                        rowCount={responseArry?.length || 0}
                        paginationMode={"client"}
                        getRowId={(row) => row.id}
                    />
                </Box>
            </MUIModal>

            <FormProvider methods={methodsBulkUpload} onSubmit={handleSubmitBulkUpload(onSubmitBulkUpload)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ maxWidth: 480 }}>
                        <RHFFileField name="orderFile" accept={['xlsx']} fileType={['xlsx']} label="Click to upload XLSX file" />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <LoadingButton
                            startIcon={<FileUploadIcon />}
                            type="submit"
                            loading={isUploadingFile}
                            variant="contained"
                            size="small"
                            disableElevation
                            sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                        >
                            Upload
                        </LoadingButton>
                        <Button variant="contained" size="small" disableElevation onClick={resetForm}
                            sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                            Reset
                        </Button>
                        <LoadingButton
                            loading={loading}
                            startIcon={<DownloadRounded />}
                            variant="outlined"
                            size="small"
                            onClick={handleDownloadSampleClick}
                            sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'none', borderRadius: '6px', color: '#1565c0', borderColor: '#1565c0', '&:hover': { bgcolor: '#eff6ff' } }}
                        >
                            Sample
                        </LoadingButton>
                    </Box>
                </Box>
            </FormProvider>
        </>
    );
};
